import React, { useContext } from 'react';
import Playlist from '../../../models/playlist/playlist';
import SyncDirection from '../../../models/playlist/syncDirection';
import SyncSource from '../../../models/playlist/syncSource';
import {
  playlistSources,
  pushesToSource,
  replaceSource,
  setSourceDirection,
  sourceDirection,
  unlinkSource,
} from '../../../models/playlist/syncSourceHelpers';
import SyncType from '../../../models/playlist/syncType';
import { LibraryContext } from '../../../services/library/libraryService';
import { Context } from '../../../services/_globalContext/context';
import { Keys, messages } from '../../../settings';
import { preventDefaultStopPropogation } from '../../../utilities/preventDefault';
import FadilaMenuItem from '../../common/contextMenu/fadilaMenuItem';
import FadilaSubMenu from '../../common/contextMenu/fadilaSubMenu';
import useYouTubeWriteAccess from '../../common/hooks/useYouTubeWriteAccess';
import s from './playlistRow.module.scss';

interface Props {
  playlist: Playlist;
}

/** What each service is called in this menu, and which key its messages use. */
const serviceNames: { [P in SyncType]?: string } = {
  [SyncType.YouTubePlaylist]: 'YouTube',
  [SyncType.SoundCloudPlaylist]: 'SoundCloud',
};

function directionsFor(service: string): Array<[SyncDirection, string]> {
  return [
    [SyncDirection.Both, 'Two-way'],
    [SyncDirection.FromSource, `${service} → Fadila only`],
    [SyncDirection.ToSource, `Fadila → ${service} only`],
  ];
}

/**
 * One submenu per remote playlist this playlist is synced with: pick the
 * direction, grant write access if the service needs it, or unlink.
 * Rendered inside the playlist row's context menu; nothing when unsynced.
 *
 * YouTube and SoundCloud both appear here. They differ in one respect: a
 * two-way YouTube link needs a permission the user grants separately, so
 * that menu offers to ask for it. SoundCloud needs nothing beyond the
 * sign-in, so its submenu is just directions and unlink.
 */
export default function PlaylistSyncMenu({ playlist }: Props) {
  const ctx = useContext(Context);
  const libCtx = useContext(LibraryContext);
  const { canWriteYouTube, ensureYouTubeWriteAccess } = useYouTubeWriteAccess();

  const sources = playlistSources(playlist);

  if (!sources.length) {
    return null;
  }

  function current() {
    return libCtx.playlists.find((x) => x.id === playlist.id) || playlist;
  }

  function changeDirection(source: SyncSource, direction: SyncDirection) {
    libCtx.updatePlaylist(setSourceDirection(current(), source, direction));

    if (source.type === SyncType.YouTubePlaylist && direction !== SyncDirection.FromSource) {
      ensureYouTubeWriteAccess();
    }
  }

  function unlink(source: SyncSource) {
    libCtx.updatePlaylist(unlinkSource(current(), source));
    ctx.callSnackbar(
      messages(
        source.type === SyncType.SoundCloudPlaylist
          ? Keys.soundCloudSyncUnlinked
          : Keys.youTubeSyncUnlinked,
      ),
      false,
    );
  }

  /** Forget what this source gave up on and sync it again straight away. */
  function retrySkipped(source: SyncSource) {
    libCtx.updatePlaylist(
      replaceSource(current(), {
        ...source,
        skippedIds: undefined,
        lastUpdated: -1,
      }),
    );
  }

  return (
    <>
      {sources.map((source) => {
        const service = serviceNames[source.type] || 'Remote';
        const isYouTube = source.type === SyncType.YouTubePlaylist;

        return (
          <FadilaSubMenu
            key={`${source.type}|${source.sourceId}`}
            title={`${service}: ${source.name || source.sourceId}`}
            preventCloseOnClick
            onClick={(ev: React.MouseEvent) => ev.stopPropagation()}
          >
            {directionsFor(service).map(([direction, label]) => (
              <FadilaMenuItem
                key={direction}
                onClick={preventDefaultStopPropogation(() => changeDirection(source, direction))}
              >
                {sourceDirection(source) === direction ? '✓ ' : ''}
                {label}
              </FadilaMenuItem>
            ))}
            {!!(source.skippedIds && source.skippedIds.length) && (
              <FadilaMenuItem
                onClick={preventDefaultStopPropogation(() => retrySkipped(source))}
                attributes={{
                  title:
                    `These tracks failed to sync in a way that retrying could not fix, ` +
                    `usually because ${service} no longer has them. Press to try once more.`,
                }}
              >
                Retry {source.skippedIds.length} skipped
              </FadilaMenuItem>
            )}
            {isYouTube && !canWriteYouTube && pushesToSource(source) && (
              <FadilaMenuItem
                onClick={preventDefaultStopPropogation(() => ensureYouTubeWriteAccess())}
                attributes={{
                  title: 'Fadila cannot change your YouTube playlists until you allow it',
                }}
              >
                Allow YouTube changes…
              </FadilaMenuItem>
            )}
            <FadilaMenuItem
              onClick={preventDefaultStopPropogation(() => unlink(source))}
              attributes={{
                className: s.deleteMenuItem,
                title: 'Stop syncing. Removes nothing from either playlist.',
              }}
            >
              Unlink
            </FadilaMenuItem>
          </FadilaSubMenu>
        );
      })}
    </>
  );
}
